import { createElement, HTMLAttributes, PropsWithChildren } from 'react';

import { cn } from '../../../helpers/classBuilders';

export type Levels = 1 | 2 | 3 | 4 | 5 | 6;

type HeadingLevels = Extract<keyof HTMLElementTagNameMap, `h${Levels}`>;

const BASE_CLASSNAME = 'c-Heading';

/**
 * Font size per visual level, on Tailwind's own scale, including the
 * line-height each stock utility bundles. The two apps that use Heading —
 * portal and portalbackoffice — declare the same 24/20/16/14/12/10 scale, and
 * the first five land exactly on stock utilities.
 *
 * Level 6 is the one gap: Tailwind's smallest step is `text-xs` (12px), so 10px
 * needs an arbitrary value, and arbitrary sizes bundle no line-height — level 6
 * inherits its parent's instead.
 */
const levelSizeClasses: Record<Levels, string> = {
  1: 'text-2xl',
  2: 'text-xl',
  3: 'text-base',
  4: 'text-sm',
  5: 'text-xs',
  6: 'text-[10px]',
};

/**
 * Typography the platform owns rather than the consumer: every heading is bold,
 * in the theme font stack.
 */
const platformTypography = 'font-(family-name:--font-stack) font-bold';

/**
 * Semantic colour. `default` is the gray-900 a title carries; `light` drops to
 * gray-600 — the body text colour — for headings that should read as a section
 * label rather than a title.
 *
 * This is the sanctioned way to soften a heading. It exists so nobody needs a
 * `text-gray-600!` override in `className`, which the merge order below would
 * otherwise be the only way to land.
 */
const colorClasses = {
  default: 'text-gray-900',
  light: 'text-gray-600',
} as const;

interface HeadingProps
  extends PropsWithChildren<HTMLAttributes<HTMLHeadingElement>> {
  level?: Levels;
  tag?: HeadingLevels;
  color?: keyof typeof colorClasses;
}

export const Heading = ({
  children,
  level = 1,
  className,
  tag,
  color = 'default',
  ...props
}: HeadingProps) => {
  const Element = tag || (`h${level}` as HeadingLevels);

  return createElement(
    Element,
    {
      /**
       * Ordering is the API contract, not a formatting detail:
       *
       * - `c-Heading` carries no styles. It stays as a selector hook — the
       *   Reports print stylesheets target `h2.c-Heading` / `h3.c-Heading`.
       * - The margin/padding reset goes *before* `className` so consumers keep
       *   full control of spacing (`mb-4`, `mt-2`).
       * - Colour, weight, font stack and level sizing go *after* `className`,
       *   so tailwind-merge drops a plain `text-red-500` or `text-sm`. Titles
       *   are gray-900 at their level size by default and cannot drift by
       *   accident — use the `color` prop for a softer heading.
       * - A deliberate exception still works: an important class such as
       *   `text-brand-primary!` lands in a different merge group and wins in
       *   the cascade, which makes every deviation explicit at the call site.
       */
      className: cn(
        BASE_CLASSNAME,
        'm-0 p-0',
        className,
        platformTypography,
        colorClasses[color],
        levelSizeClasses[level],
      ),
      ...props,
    },
    children,
  );
};
